import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { UserRole } from '@/types/auth'
import { useAuth } from '@/hooks/useAuth'

interface RoleOverrideContextType {
  // Current effective role (either original or overridden)
  effectiveRole: UserRole
  // Original role from auth
  originalRole: UserRole | null
  // Whether role is currently overridden
  isRoleOverridden: boolean
  // Override the role
  setRoleOverride: (role: UserRole) => void
  // Clear override and return to original role
  clearRoleOverride: () => void
  // Can the current user override roles (must be admin)
  canOverrideRole: boolean
}

const RoleOverrideContext = createContext<RoleOverrideContextType | undefined>(undefined)

interface RoleOverrideProviderProps {
  children: ReactNode
}

const STORAGE_KEY = 'role-override'

export function RoleOverrideProvider({ children }: RoleOverrideProviderProps) {
  const { user, overrideUserRole, clearRoleOverride: clearAuthRoleOverride, originalRole: authOriginalRole } = useAuth()

  // Only admins can override roles (check both current role and originalRole)
  const canOverrideRole = user?.role === 'admin' || user?.originalRole === 'admin'
  const originalRole = authOriginalRole

  // Check if currently overridden
  const isRoleOverridden = user?.originalRole !== undefined
  const effectiveRole = user?.role || 'employee'

  const setRoleOverride = (role: UserRole) => {
    if (!canOverrideRole) {
      console.error('❌ Only admins can override roles')
      return
    }

    overrideUserRole(role)
  }

  const clearRoleOverride = () => {
    clearAuthRoleOverride()
  }

  const value: RoleOverrideContextType = {
    effectiveRole,
    originalRole,
    isRoleOverridden,
    setRoleOverride,
    clearRoleOverride,
    canOverrideRole
  }

  return (
    <RoleOverrideContext.Provider value={value}>
      {children}
    </RoleOverrideContext.Provider>
  )
}

export function useRoleOverride() {
  const context = useContext(RoleOverrideContext)
  if (context === undefined) {
    throw new Error('useRoleOverride must be used within a RoleOverrideProvider')
  }
  return context
}
